import {useState} from 'react';
import {Alert, Form} from 'antd';
import {Button} from '@dabafinance/react-components';
import DBModal from 'components/modal/modal';
import {
  describeAudience,
  groupRows,
  headerLine,
  splitValues,
} from '../utils/audienceSummary';
import styles from './audienceReview.module.scss';

// Headers and the summary card read `getFieldsValue()`: the fields on
// screen, which are exactly what a submit sends. A value left in the store by
// a hidden field is not.

/** A field's values as chips, "or" between them. */
const ValueChips = ({text}) => (
  <div className={styles.chips}>
    {splitValues(text).map((value, index) => (
      <span key={value} className={styles.chipWrap}>
        {index > 0 && <span className={styles.or}>or</span>}
        <span className={styles.chip}>{value}</span>
      </span>
    ))}
  </div>
);

/** A section's conditions, grouped by theme, as tiles of value chips. */
const ConditionGroups = ({section}) => (
  <div className={styles.groupList}>
    {groupRows(section).map(group => (
      <section key={group.title ?? 'other'} className={styles.group}>
        {group.title && <h4 className={styles.groupTitle}>{group.title}</h4>}
        <div className={styles.tiles}>
          {group.rows.map(({label, text}) => (
            <div key={label} className={styles.tile}>
              <span className={styles.tileLabel}>{label}</span>
              <ValueChips text={text} />
            </div>
          ))}
        </div>
      </section>
    ))}
  </div>
);

/** One section in plain words: its conditions grouped by theme. */
const SectionExplanation = ({section, onClose}) => {
  const {title, explain, rows} = section;
  return (
    <div className={styles.explain}>
      <span className={styles.eyebrow}>Full explanation</span>
      <h2 className={styles.modalTitle}>{title}</h2>
      {explain && <p className={styles.lead}>{explain}</p>}

      <div className={styles.summary}>
        <span className={styles.summaryCount}>{rows.length}</span>
        <span>
          <strong>{rows.length === 1 ? 'condition' : 'conditions'} set</strong>
          {rows.length > 1 && ' · a user must match all of them'}
        </span>
      </div>

      <div className={styles.groups}>
        <ConditionGroups section={section} />
      </div>

      <div className={styles.explainFooter}>
        <span className={styles.muted}>
          {section.key === 'holdings'
            ? 'How several holdings combine is set by Match.'
            : 'Several values in one field: any one is enough.'}
        </span>
        <Button label="Done" onClick={onClose} />
      </div>
    </div>
  );
};

const stop = event => event.stopPropagation();

/**
 * A filter section's collapse header: its title, how many fields are set, one
 * short line naming them, and a link to the full explanation.
 */
export const SectionHeader = ({sectionKey, title, hint, lookups}) => {
  const [open, setOpen] = useState(false);
  return (
    <Form.Item noStyle shouldUpdate>
      {form => {
        const section = describeAudience(form.getFieldsValue(), lookups).find(
          item => item.key === sectionKey,
        );
        const rows = section?.rows ?? [];
        return (
          <div className={styles.header}>
            <div className={styles.headerTop}>
              <span className={styles.headerTitle}>{title}</span>
              {rows.length > 0 && (
                <span className={styles.count}>{rows.length} set</span>
              )}
              {rows.length > 0 && (
                <button
                  type="button"
                  className={styles.explainLink}
                  onKeyPress={stop}
                  onClick={event => {
                    // The header toggles the section; this should not.
                    event.stopPropagation();
                    setOpen(true);
                  }}>
                  Full explanation
                </button>
              )}
            </div>
            <span
              className={rows.length ? styles.headerSet : styles.headerHint}>
              {rows.length ? headerLine(rows) : hint}
            </span>
            {/* Portal events still bubble to the header in React; stop them
                so clicks in the modal do not toggle the section. */}
            <span onClick={stop} onKeyPress={stop}>
              <DBModal
                isOpen={open && rows.length > 0}
                handleClose={() => setOpen(false)}
                width={720}
                destroyOnClose
                content={
                  section && (
                    <SectionExplanation
                      section={section}
                      onClose={() => setOpen(false)}
                    />
                  )
                }
              />
            </span>
          </div>
        );
      }}
    </Form.Item>
  );
};

/** Where a section's rows combine: shown at the top of its body. */
export const SectionRule = ({children}) => (
  <p className={styles.rule}>
    {children} Leave a field empty to ignore it; a field with several values
    matches any one of them.
  </p>
);

/** The audience: where it starts, then each filled section, joined by AND. */
const AudienceBreakdown = ({sections}) => {
  const [base, ...filters] = sections;
  const active = filters.filter(section => section.rows.length);
  return (
    <div className={styles.breakdown}>
      <div className={styles.startsFrom}>
        <span className={styles.tileLabel}>Starts from</span>
        <strong>{base.rows[0]?.text}</strong>
      </div>
      {active.length === 0 ? (
        <p className={styles.muted}>
          No other filters: everyone in this audience receives it, subject to
          their notification preferences.
        </p>
      ) : (
        active.map(section => (
          <div key={section.key}>
            <div className={styles.andDivider}>
              <span>and</span>
            </div>
            <div className={styles.block}>
              <div className={styles.blockHead}>
                <span className={styles.blockTitle}>{section.title}</span>
                <span className={styles.count}>
                  {section.rows.length}{' '}
                  {section.rows.length === 1 ? 'condition' : 'conditions'}
                </span>
              </div>
              {section.rule && (
                <p className={styles.blockRule}>{section.rule}</p>
              )}
              <ConditionGroups section={section} />
            </div>
          </div>
        ))
      )}
    </div>
  );
};

/** Label/value tiles for the message and schedule; empty values are skipped. */
const InfoTiles = ({items}) => (
  <div className={styles.tiles}>
    {items
      .filter(([, text]) => text)
      .map(([label, text]) => (
        <div key={label} className={styles.tile}>
          <span className={styles.tileLabel}>{label}</span>
          <span className={styles.tileValue}>{text}</span>
        </div>
      ))}
  </div>
);

const Step = ({number, title, children}) => (
  <section className={styles.reviewStep}>
    <h3 className={styles.reviewStepTitle}>
      <span className={styles.reviewStepNo}>{number}</span>
      {title}
    </h3>
    {children}
  </section>
);

/**
 * Final review before the notification is scheduled or updated: the message,
 * the full audience section by section, and when it goes out.
 */
export const SendReviewModal = ({
  open,
  onClose,
  onConfirm,
  confirmLabel,
  values,
  lookups,
  message = [],
  schedule = [],
  holdingsPreview,
}) => (
  <DBModal
    isOpen={open}
    handleClose={onClose}
    width={820}
    destroyOnClose
    content={
      open && (
        <div className={styles.review}>
          <span className={styles.eyebrow}>Before you send</span>
          <h2 className={styles.modalTitle}>Review and confirm</h2>
          <p className={styles.lead}>
            Check what goes out, who receives it and when. Nothing is sent until
            you confirm.
          </p>

          <div className={styles.reviewBody}>
            <Step number={1} title="Message">
              <InfoTiles items={message} />
            </Step>

            <Step number={2} title="Audience">
              <AudienceBreakdown sections={describeAudience(values, lookups)} />
              {holdingsPreview === 0 && (
                <Alert
                  type="warning"
                  showIcon
                  className={styles.alert}
                  message="The last holdings preview found no users matching this audience."
                  description="Holdings are checked again when it is sent, but as things stand it would reach nobody."
                />
              )}
              {holdingsPreview > 0 && (
                <Alert
                  type="info"
                  showIcon
                  className={styles.alert}
                  message={`Last holdings preview: about ${holdingsPreview.toLocaleString()} recipients.`}
                />
              )}
            </Step>

            <Step number={3} title="Schedule">
              <InfoTiles items={schedule} />
            </Step>
          </div>

          <div className={styles.explainFooter}>
            <span className={styles.muted}>
              Several values in one field: any one is enough.
            </span>
            <div className={styles.actions}>
              <Button
                type="secondary"
                label="Back to editing"
                onClick={onClose}
              />
              <Button label={confirmLabel} onClick={onConfirm} />
            </div>
          </div>
        </div>
      )
    }
  />
);
